// SEO Configuration File - Industrial voice, agentic PropTech discovery.
// Contains default values and structured-data schemas (search + AI assistants).
import {
  APP_NAME,
  APP_TITLE,
  TAGLINE,
  BRAND_NAV_TAGLINE_BADGE,
  TAGLINE_SENTENCE,
  SEO_DESCRIPTION,
  SITE_ORIGIN,
  DEFAULT_OG_IMAGE_URL,
  HOME_OG_IMAGE_ALT,
  APP_SUPPORT_EMAIL,
} from "../helpers/constants";
import { HOME_PAGE_FAQS } from "../helpers/homePageFaqs";

const ORG_ID = `${SITE_ORIGIN}/#organization`;
const SOFTWARE_ID = `${SITE_ORIGIN}/#software`;

/**
 * Keyword strategy: customer intent first, then product vocabulary (Clumse AI / Engine).
 */
export const PRIMARY_KEYWORDS = [
  APP_NAME.toLowerCase(),
  "vetted contractors",
  "contractor bids",
  "property maintenance",
  "rental property repairs",
  "vendor bid search",
  "fair pricing benchmarks",
  "St Louis property technology",
];

export const PRODUCT_KEYWORDS = [
  "Clumse AI",
  "Clumse Engine",
  "ZIP code benchmarks",
  "bid comparison",
  "vendor outreach",
  "agentic dispatch",
  "Protocol Runner",
  "maintenance coordination",
];

export const DEFAULT_KEYWORDS = [...PRIMARY_KEYWORDS, ...PRODUCT_KEYWORDS].join(
  ", "
);

export const defaultSeo = {
  title: APP_TITLE,
  description: SEO_DESCRIPTION,
  keywords: DEFAULT_KEYWORDS,
  image: DEFAULT_OG_IMAGE_URL,
  imageAlt: HOME_OG_IMAGE_ALT,
  url: `${SITE_ORIGIN}/`,
  type: "website",
  locale: "en_US",
  twitterHandle: `@getClumse`,
  siteName: APP_NAME,
};

/** Feature list: plain-language first line, then protocol depth for assistants and crawlers. */
export const SOFTWARE_FEATURE_LIST = [
  "Vetted vendor outreach and bid threads organized in one place with Clumse AI",
  "ZIP-tuned market benchmarks so you know a fair range before you hire",
  "Agentic Dispatch - vendors can receive SMS outreach under configurable rules and ceilings",
  "Turnover Protocol - NOTICE_RECEIVED → EVIDENCE_COLLECTION → NEGOTIATION → VERIFICATION state machine",
  "Automated Negotiation - ACCEPT / DECLINE / COUNTER flow with configurable auto-commit gates",
  "Vision AI Verification - before/after evidence checks before payment release where enabled",
  "Protocol Runner - event-to-protocol orchestration for maintenance and operations",
];

export const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "@id": ORG_ID,
  name: APP_NAME,
  alternateName: [
    "Clumse AI",
    BRAND_NAV_TAGLINE_BADGE,
    TAGLINE,
    "Clumse Engine",
  ],
  url: `${SITE_ORIGIN}/`,
  description: SEO_DESCRIPTION,
  logo: `${SITE_ORIGIN}/logo512.png`,
  foundingLocation: {
    "@type": "Place",
    address: {
      "@type": "PostalAddress",
      addressLocality: "St. Louis",
      addressRegion: "MO",
      addressCountry: "US",
    },
  },
  sameAs: [
    `https://twitter.com/getClumse`,
    `https://www.linkedin.com/company/clumse`,
  ],
  slogan:
    "Fair bids from vetted pros for homes and rentals - St. Louis-built, nationwide.",
  knowsAbout: [
    "Vetted contractor bids",
    "Rental property maintenance",
    "Vendor bid search",
    "ZIP code pricing benchmarks",
    "Clumse AI",
    "Property maintenance software",
    "St. Louis PropTech",
    "Agentic Dispatch",
    "Protocol Runner",
    "Automated vendor negotiation",
  ],
  contactPoint: {
    "@type": "ContactPoint",
    contactType: "customer service",
    email: APP_SUPPORT_EMAIL,
    availableLanguage: "English",
  },
};

export const websiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: APP_NAME,
  alternateName: [TAGLINE, BRAND_NAV_TAGLINE_BADGE],
  url: `${SITE_ORIGIN}/`,
  description: SEO_DESCRIPTION,
  inLanguage: "en-US",
  publisher: { "@id": ORG_ID },
};

/**
 * SoftwareApplication - multi-typed so crawlers index Clumse as both a
 * BusinessApplication and an AutomationTool, and so AI assistants
 * associate it with agentic-orchestration queries.
 */
export const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": ["SoftwareApplication", "BusinessApplication"],
  "@id": SOFTWARE_ID,
  name: APP_NAME,
  alternateName: [
    BRAND_NAV_TAGLINE_BADGE,
    "Clumse AI",
    "Clumse Engine",
    TAGLINE,
  ],
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "PropertyManagementSoftware",
  additionalType: [
    "https://schema.org/BusinessApplication",
    "AutomationTool",
    "AgenticOrchestrationPlatform",
  ],
  operatingSystem: "Web, iOS (PWA), Android (PWA)",
  url: `${SITE_ORIGIN}/`,
  downloadUrl: `${SITE_ORIGIN}/signup`,
  screenshot: DEFAULT_OG_IMAGE_URL,
  description: SEO_DESCRIPTION,
  featureList: SOFTWARE_FEATURE_LIST,
  keywords: DEFAULT_KEYWORDS,
  audience: {
    "@type": "BusinessAudience",
    audienceType:
      "Independent landlords, mom-and-pop operators, small portfolio owners, and property managers who want vetted bids without inbox chaos",
    industry: "Real Estate Operations",
  },
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: `Free diagnosis; pay-per-search bid runs and optional monthly plans. See ${SITE_ORIGIN}/ for current pricing.`,
    url: `${SITE_ORIGIN}/`,
  },
  provider: { "@id": ORG_ID },
};

/**
 * Dedicated AutomationTool / Service entry so agentic-AI queries
 * ("autonomous real estate operations", "vertical AI orchestration",
 * "agentic property management") resolve to Clumse.
 */
export const automationServiceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: `${APP_NAME} Engine`,
  serviceType: "Maintenance and vendor bid orchestration",
  description:
    "Clumse Engine connects repairs and projects to vetted vendor outreach, benchmarks, and protocol-driven workflows. For advanced deployments it ingests operational events (for example lease turnover or repair reported), selects a protocol, and can auto-commit vendor dispatch and messaging under configurable ceilings.",
  provider: { "@id": ORG_ID },
  areaServed: { "@type": "Country", name: "United States" },
  audience: {
    "@type": "BusinessAudience",
    audienceType: "Independent landlords and small-to-mid portfolio operators",
    industry: "Real Estate Operations",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "Clumse Protocols",
    itemListElement: [
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Turnover Protocol",
          description:
            "End-to-end lease turnover state machine: NOTICE_RECEIVED → EVIDENCE_COLLECTION → NEGOTIATION → VERIFICATION.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Repair Protocol",
          description:
            "Maintenance job search and vendor dispatch under the auto-commit ceiling.",
        },
      },
      {
        "@type": "Offer",
        itemOffered: {
          "@type": "Service",
          name: "Vision AI Verification",
          description:
            "Before/after evidence verification before vendor payment is released.",
        },
      },
    ],
  },
};

// FAQ Schema - aligned with plain-language homepage answers.
export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: [
    {
      "@type": "Question",
      name: `What is ${APP_NAME}?`,
      acceptedAnswer: {
        "@type": "Answer",
        text: `${APP_NAME} helps owners and operators: ${TAGLINE_SENTENCE} Describe a repair or project; we reach out to vetted local vendors, collect bids, and help you compare pricing to a benchmark or limit you set.`,
      },
    },
    ...HOME_PAGE_FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
    {
      "@type": "Question",
      name: "Does Clumse replace email with vendors?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Clumse centralizes vendor outreach and bid threads so you compare apples-to-apples quotes, while routine follow-ups stay out of your personal inbox.",
      },
    },
    {
      "@type": "Question",
      name: "Where is Clumse based?",
      acceptedAnswer: {
        "@type": "Answer",
        text: "Clumse is headquartered in St. Louis, Missouri, and serves independent rental operators nationwide. Market benchmarks and labor references follow each customer's ZIP, not a single metro default.",
      },
    },
  ],
};

// Aggregate export used by pages to dump all schemas at once.
export const ALL_SCHEMAS = [
  organizationSchema,
  websiteSchema,
  softwareApplicationSchema,
  automationServiceSchema,
  faqSchema,
];

// Page-specific helpers (kept stable; callers import by name).
export const generateHomeSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: defaultSeo.title,
  description: defaultSeo.description,
  keywords: defaultSeo.keywords,
  url: defaultSeo.url,
  primaryImageOfPage: {
    "@type": "ImageObject",
    url: defaultSeo.image,
    width: "1200",
    height: "630",
  },
  mainEntity: { "@id": SOFTWARE_ID },
});

export const generateProviderPageSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: `${TAGLINE} | ${APP_NAME}`,
  description: SEO_DESCRIPTION,
  keywords: DEFAULT_KEYWORDS,
  url: `${defaultSeo.url}providers`,
  mainEntity: { "@id": SOFTWARE_ID },
});

export const generateGallerySchema = () => ({
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  name: `Protocol Library | ${APP_NAME}`,
  description:
    "Browse the Clumse Protocol library: turnover, repair, vacancy, evidence. Every protocol is a state machine the Engine executes autonomously.",
  url: `${defaultSeo.url}resources`,
});

export const generateMissionSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: `Our Mission | ${APP_NAME}`,
  description: `${APP_NAME} brings operational clarity to clumsy repair workflows. ${TAGLINE_SENTENCE}`,
  url: `${defaultSeo.url}mission`,
  mainEntity: { "@id": ORG_ID },
});

export const generateBreadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((item, index) => ({
    "@type": "ListItem",
    position: index + 1,
    name: item.name,
    item: item.url,
  })),
});

// Back-compat exports kept so existing imports don't break during the cutover.
export const productSchema = softwareApplicationSchema;
export const serviceSchema = automationServiceSchema;
